// 주문 내역 화면.
// 작업계획서 4.6: 주문 조회(상태별). 한 번에 전체를 받아 클라이언트에서 탭(진행중/완료/취소)으로 그루핑한다.
//  - "진행중" 은 여러 상태(주문/접수/준비중/준비완료)를 묶은 손님 관점 그룹이라 단일 status 쿼리보다 클라이언트 분류가 자연스럽다.
//
// 손님 카피 톤([[feedback-customer-copy-tone]]): 거절/취소는 모두 "취소" 그룹으로 보인다.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ORDER_STATUS,
  ORDER_STATUS_LABEL_KO,
  ORDER_TYPE_LABEL_KO,
  formatDesiredPickup,
  type OrderDto,
  type OrderStatus,
} from "@oo/shared";
import { listOrders } from "@/features/orders/ordersApi.ts";

// 손님 관점 탭. 내부 상태 집합으로 매핑한다.
type Tab = "ongoing" | "completed" | "canceled";
const TAB_STATUSES: Record<Tab, OrderStatus[]> = {
  ongoing: [
    ORDER_STATUS.RECEIVED,
    ORDER_STATUS.ACCEPTED,
    ORDER_STATUS.PREPARING,
    ORDER_STATUS.READY,
  ],
  completed: [ORDER_STATUS.COMPLETED],
  canceled: [ORDER_STATUS.REJECTED, ORDER_STATUS.CANCELED],
};
const TAB_LABEL: Record<Tab, string> = {
  ongoing: "진행중",
  completed: "완료",
  canceled: "취소",
};

// 내 주문 내역 화면.
export default function OrdersPage() {
  const [tab, setTab] = useState<Tab>("ongoing");
  const ordersQuery = useQuery({ queryKey: ["orders"], queryFn: () => listOrders() });

  const filtered = useMemo(() => {
    const all = ordersQuery.data ?? [];
    const allowed = new Set(TAB_STATUSES[tab]);
    return all.filter((o) => allowed.has(o.status));
  }, [ordersQuery.data, tab]);

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-28">
      <h1 className="text-2xl font-bold">주문 내역</h1>

      {/* 탭 */}
      <div className="flex gap-1 rounded-lg bg-ink-100 p-1">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`min-h-touch flex-1 rounded-md text-sm font-medium ${
              tab === t ? "bg-white shadow-sm" : "text-ink-600"
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {ordersQuery.isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-ink-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-ink-300 p-10 text-center">
          <p className="text-base font-medium">{TAB_LABEL[tab]} 주문이 없습니다.</p>
          <Link to="/category" className="mt-1 text-sm text-ink-600 underline">
            상품 보러 가기
          </Link>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {filtered.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </ul>
      )}
    </main>
  );
}

// 주문 한 건 카드.
function OrderCard({ order }: { order: OrderDto }) {
  // 항목 요약: 첫 상품명 + 외 N건.
  const firstName = order.items[0]?.name ?? "주문 상품";
  const moreCount = order.itemCount - 1;
  const summary = moreCount > 0 ? `${firstName} 외 ${moreCount}건` : firstName;
  const isCanceled =
    order.status === ORDER_STATUS.REJECTED || order.status === ORDER_STATUS.CANCELED;
  const isDone = order.status === ORDER_STATUS.COMPLETED;

  return (
    <li>
      <Link
        to={`/me/orders/${order.id}`}
        className="flex flex-col gap-2 rounded-lg border border-ink-200 bg-white p-4 hover:bg-ink-50"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-ink-600 tabular-nums">{order.orderNo}</span>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              isCanceled
                ? "bg-ink-200 text-ink-600"
                : isDone
                  ? "bg-success-50 text-success-700"
                  : "bg-brand-600 text-white"
            }`}
          >
            {ORDER_STATUS_LABEL_KO[order.status]}
          </span>
        </div>
        <p className="truncate text-sm font-medium text-ink-900">{summary}</p>
        <div className="flex items-baseline justify-between text-xs text-ink-600">
          <span>
            {ORDER_TYPE_LABEL_KO[order.type]} ·{" "}
            {formatDesiredPickup(order.desiredDate, order.desiredTime)}
          </span>
          <span className="text-sm font-bold tabular-nums text-ink-900">
            {order.totalKrw.toLocaleString()}원
          </span>
        </div>
      </Link>
    </li>
  );
}
